// NOTE: Supports cases where `content-type` is other than `json`
import { Auth } from 'aws-amplify';

const getBody = <T>(c: Response | Request): Promise<T> => {
    const contentType = c.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
        return c.json();
    }

    if (contentType && contentType.includes('application/pdf')) {
        return c.blob() as Promise<T>;
    }

    return c.text() as Promise<T>;
};

export const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production';

// Determine the base URL depending on environment
const getBaseUrl = (): string => {
    // Check if we're in a production environment

    // Use the production URL in production, localhost in development
    return isProduction ? 'https://api.tamnuni.com' : 'http://localhost:3000';
};

export interface ContextUrl {
    url: string;
    method: string;
    signal?: AbortSignal;
    params?: Record<string, any>;
}

// NOTE: Update just base url - For development, access the API directly
const getUrl = (contextUrl: string | URL | Request | ContextUrl): string => {
    // Get the appropriate base URL for the current environment
    const baseUrl = getBaseUrl();

    // Using configured API base URL
    // Handle special case for object with url property (from generated code)
    if (typeof contextUrl === 'object' && 'url' in contextUrl && typeof contextUrl.url === 'string') {
        const path = contextUrl.url;
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        let fullUrl = `${baseUrl}/${cleanPath}`;

        // Handle query parameters if they exist
        if (typeof contextUrl === 'object' && 'params' in contextUrl && contextUrl.params) {
            const queryParams = new URLSearchParams();

            // Add all non-null parameters to the URLSearchParams
            Object.entries(contextUrl?.params).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    queryParams.append(key, String(value));
                }
            });

            const queryString = queryParams.toString();
            if (queryString) {
                fullUrl += `?${queryString}`;
            }
        }

        return fullUrl;
    }

    // Handle other cases
    const urlString =
        typeof contextUrl === 'string'
            ? contextUrl
            : contextUrl instanceof URL
              ? contextUrl.pathname + contextUrl.search
              : contextUrl instanceof Request
                ? new URL(contextUrl.url).pathname + new URL(contextUrl.url).search
                : String(contextUrl);

    // Remove leading slash if present
    const cleanPath = urlString.startsWith('/') ? urlString.substring(1) : urlString;

    return `${baseUrl}/${cleanPath}`;
};

// Get Cognito token asynchronously
const getAuthToken = async (): Promise<string | null> => {
    try {
        const session = await Auth.currentSession();
        // Prefer access token for API authorization
        const token = session.getAccessToken().getJwtToken(); //TODO: check if Id token works
        return token;
    } catch (error) {
        // Failed to get auth token
        return null;
    }
};

// NOTE: Add headers
const getHeaders = async (headers?: HeadersInit | undefined): Promise<HeadersInit> => {
    const token = await getAuthToken();

    const baseHeaders: Record<string, string> = {
        // Only spread headers if they exist
        ...(headers ? (headers as Record<string, string>) : {}),
        'Content-Type': 'application/json',
        // Add other common headers that might be needed for CORS
        Accept: 'application/json',
    };

    // If we have a token, include it in the Authorization header
    if (token) {
        baseHeaders['Authorization'] = `Bearer ${token}`;
        // Using Auth token in request
    } else {
        // No auth token available for request
    }

    return baseHeaders;
};

// Check if a URL is cross-origin
const isCrossOrigin = (url: string | URL | { url: string; method: string; params?: Record<string, any> }): boolean => {
    // Handle URL object from generated code
    if (typeof url === 'object' && 'url' in url && typeof url.url === 'string') {
        // Extract the URL string
        const urlString = url.url;
        // If URL is relative, it's same-origin
        if (urlString.startsWith('/')) return false;

        try {
            // Check if the URL has a different origin than the current page
            const parsedUrl = new URL(urlString, window.location.origin);
            return parsedUrl.origin !== window.location.origin;
        } catch (e) {
            // If URL parsing fails, assume cross-origin
            return true;
        }
    }

    // Handle string or URL object
    const urlString = typeof url === 'string' ? url : url.toString();

    // If URL is relative, it's same-origin
    if (urlString.startsWith('/')) return false;

    try {
        // Check if the URL has a different origin than the current page
        const parsedUrl = new URL(urlString, window.location.origin);
        return parsedUrl.origin !== window.location.origin;
    } catch (e) {
        // If URL parsing fails, assume cross-origin
        return true;
    }
};

// Handle preflight check for cross-origin requests
const performPreflightIfNeeded = async (
    url: string | URL | { url: string; method: string; params?: Record<string, any> },
    headers: HeadersInit,
): Promise<void> => {
    if (!isCrossOrigin(url)) {
        // Same origin request, no preflight needed
        return;
    }

    // Performing manual OPTIONS preflight check for cross-origin request

    try {
        // Convert url to string if it's an object
        const urlString = typeof url === 'object' && 'url' in url ? url.url : String(url);

        const response = await fetch(urlString, {
            method: 'OPTIONS',
            headers: headers,
            mode: 'cors',
            credentials: 'include',
        });

        if (!response.ok) {
            // Preflight check failed
        } else {
            // Preflight check successful
        }
    } catch (error) {
        // Preflight check error
        // We continue anyway, as the actual request might still succeed
    }
};

export const customFetch = async <T>(
    url:
        | string
        | URL
        | Request
        | {
              url: string;
              method: string;
              signal?: AbortSignal;
              data?: any;
              headers?: HeadersInit;
              params?: Record<string, any>;
          },
    options: RequestInit = {},
): Promise<T> => {
    try {
        const requestUrl = getUrl(url);
        const requestHeaders = await getHeaders(options?.headers);

        // Fetching URL with properly formatted request path

        // Extract signal if it exists in the URL object
        let signal = undefined;
        if (typeof url === 'object' && 'signal' in url && url.signal instanceof AbortSignal) {
            signal = url.signal;
        }

        // Handle request body data if it exists in the URL object
        let body = options.body;
        if (typeof url === 'object' && 'data' in url && url.data !== undefined) {
            body = JSON.stringify(url.data);
        }

        const requestInit: RequestInit = {
            ...options,
            headers: requestHeaders,
            method: typeof url === 'object' && 'method' in url ? url.method : options.method,
            // Add credentials: 'include' for cookies
            credentials: 'include',
            // Add signal if it exists
            ...(signal && { signal }),
            // Add body if it exists
            ...(body && { body }),
        };

        // Making request with configured options

        const request = new Request(requestUrl, requestInit);
        const response = await fetch(request);

        if (!response.ok) {
            const errorText = await response.text();
            // Try to parse as JSON to get a more helpful error message
            try {
                const errorJson = JSON.parse(errorText);
                // API error occurred with JSON response
                if (errorJson.message) {
                    throw new Error(errorJson.message);
                }
            } catch (e) {
                // Couldn't parse as JSON, use the raw text or status
                const errorMessage = errorText || `API Error: ${response.status} ${response.statusText}`;
                throw new Error(errorMessage);
            }
        }

        // Response received

        // Special handling for API response
        const data = await getBody<T>(response);
        return data as T;
    } catch (error) {
        // Error in customFetch
        throw error;
    }
};
